import React from 'react';

import { makeStyles } from 'tss-react/mui';

type OrdinalBadgeSize = 'large' | 'small';

/** Diameter of the large badge; the position gutter is sized to fit it exactly. */
export const ORDINAL_BADGE_LARGE_PX = 24;

const useStyles = makeStyles<{ current: boolean; size: OrdinalBadgeSize }>()((
  theme,
  { current, size }
) => {
  const diameter = size === 'large' ? ORDINAL_BADGE_LARGE_PX : 22;

  return {
    badge: {
      alignItems: 'center',
      // The app's primary blue, so the current stop reads like a selection.
      backgroundColor: current ? theme.appColors.primary : 'transparent',
      borderRadius: 999,
      color: current ? theme.palette.common.white : theme.appColors.iconsTypeMuted,
      display: 'inline-flex',
      flexShrink: 0,
      fontSize: size === 'large' ? 14 : 13,
      fontVariantNumeric: 'tabular-nums',
      fontWeight: 600,
      height: diameter,
      justifyContent: 'center',
      lineHeight: `${diameter}px`,
      minWidth: diameter,
      padding: theme.spacing(0, 0.5),
    },
  };
});

/**
 * Plain gray numbering. Beside a position card it fills blue while that
 * position is the current stop; a rule row's number never changes. The large
 * size is for the gutter beside a position card, the small one for a rule row.
 */
export const OrdinalBadge = React.memo(function OrdinalBadge({
  className,
  current = false,
  index,
  size = 'small',
}: {
  index: number;
  className?: string;
  /** Fills the badge blue: the numbered thing is the current stop. */
  current?: boolean;
  size?: OrdinalBadgeSize;
}): JSX.Element {
  const { classes, cx } = useStyles({ current, size });

  return <span className={cx(classes.badge, className)}>{index}</span>;
});
